import { freezeProgramComposition, type ProgramCompositionObservation } from './program-composition';
export interface FrameComposition {observation: Readonly<ProgramCompositionObservation>; current(): boolean;onAccepted?(frame:AcceptedFrame):void;}
/** First-party process-local evidence; never an authorization token or pixel attestation. */
export interface AcceptedFrame {
  readonly composition?: Readonly<ProgramCompositionObservation>;
  readonly compositionCurrent?: () => boolean;
  readonly sourceId: string;
  /** Counts actual accepted writes in this output source generation. */
  readonly sequence?: number;
  readonly timestampUs: number;
  /** Conservative lower bound before the synchronous VideoFrame canvas snapshot, not a synchronized server clock. */
  readonly capturedMonotonicMs: number;
  /** Upper bound immediately after synchronous snapshot; absent legacy evidence is not runtime readiness. */
  readonly captureUpperMonotonicMs?: number;
}
let generationSequence=0;
const sources = new WeakMap<MediaStreamTrack, ProgramFrameSource>();
/** Output-owned registry, bounded independently of whether a segmenter consumes it. */
export class ProgramFrameSource {
  private sourceId = crypto.randomUUID();
  private sequence = 0;
  private sourceGeneration=++generationSequence;
  private frames = new Map<number, AcceptedFrame>();
  constructor(private readonly track: MediaStreamTrack) { sources.set(track, this); }
  get generation():number{return this.sourceGeneration;}
  get identity(): string { return this.sourceId; }
  submit(timestampUs: number, capturedMonotonicMs: number, captureUpperMonotonicMs?: number, composition?: FrameComposition): () => void {
    const sourceId = this.sourceId;
    let observation: Readonly<ProgramCompositionObservation> | undefined;
    try { if(composition?.current()) observation=freezeProgramComposition(composition.observation); } catch {}
    const current=()=>{try{return !composition||composition.current();}catch{return false;}};
    return () => {
      if (sources.get(this.track) !== this || this.sourceId !== sourceId || (composition && (!current() || !observation || observation.binding.programFrameSourceId !== sourceId))) return;
      this.frames.set(timestampUs, Object.freeze({ ...(observation?{composition:observation,compositionCurrent:current}:{}), sourceId, sequence:++this.sequence, timestampUs, capturedMonotonicMs, ...(captureUpperMonotonicMs===undefined?{}:{captureUpperMonotonicMs}) }));
      try{composition?.onAccepted?.(this.frames.get(timestampUs)!);}catch{}
      while (this.frames.size > 180) this.frames.delete(this.frames.keys().next().value!);
    };
  }
  replace(): void { this.sourceId = crypto.randomUUID(); this.sequence=0; this.sourceGeneration=++generationSequence; this.frames.clear(); }
  close(): void { this.replace(); sources.delete(this.track); }
  get(timestampUs: number): AcceptedFrame | undefined { const frame=this.frames.get(timestampUs);return frame?.compositionCurrent && !frame.compositionCurrent() ? undefined : frame; }
}
export function frameEvidence(track: MediaStreamTrack, timestampUs: number): AcceptedFrame | undefined {
  return sources.get(track)?.get(timestampUs);
}

/** Missing for captureStream fallback, closed outputs and foreign tracks. */
export function programFrameSourceId(track:MediaStreamTrack):string|undefined { return sources.get(track)?.identity; }
